import {
  SpaceBetween,
  FormField,
  Box,
  Input,
  Button,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { COMMON_CONFIGS, COMMON_LABELS } from "../default-constants";
import { CONSTRAINT_TEXT, IErrMsg, LABELS } from "./constants";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { isNull } from "../../utils/inputValidation";
import { BankAccountsUpdateRequest } from "../../features/api-interfaces";
import {
  useGetAddressByPostalCodeQuery,
  useGetCorpInfoByCorpNumQuery,
} from "../../features/api";
import { isEqual, isUndefined } from "lodash";
import {
  validCorporateNumber,
  validCompanyName,
  validCompanyNameKana,
  validPostalCode,
  validPaymentTargetCompanyAddress2,
  validPaymentTargetCompanyAddress1Kana,
  validPhoneNumber,
  hasCorpInfo,
} from "./validation";

interface IPaymentTargetCompanyUpdate {
  request: BankAccountsUpdateRequest;
  setRequest: Dispatch<SetStateAction<BankAccountsUpdateRequest>>;
  errMsg: IErrMsg;
  setErrMsg: Dispatch<SetStateAction<IErrMsg>>;
}

export const PaymentTargetCompanyUpdate = (
  props: IPaymentTargetCompanyUpdate
) => {
  const { request, setRequest, errMsg, setErrMsg } = props;
  const hasCorpNo = !isNull(request.corporateNumber);
  const isRequired = hasCorpNo || hasCorpInfo(request);
  const [isCorpNoSearchSkip, setIsCorpNoSearchSkip] = useState(true);
  const [isPostalCodeSearchSkip, setIsPostalCodeSearchSkip] = useState(true);
  const { data: corpInfo } = useGetCorpInfoByCorpNumQuery(
    request.corporateNumber,
    {
      skip: isCorpNoSearchSkip,
    }
  );
  const { data: address } = useGetAddressByPostalCodeQuery(request.postalCode, {
    skip: isPostalCodeSearchSkip,
  });

  useEffect(() => {
    setIsCorpNoSearchSkip(true);
    if (
      corpInfo &&
      isEqual(corpInfo.status, COMMON_CONFIGS.API.STATUS_SUCCESS)
    ) {
      // 法人番号検索APIで取得した事業者名と事業者名カナをセットする
      const { companyName, companyNameKana } = corpInfo.companyInfo;
      setRequest({ ...request, companyName, companyNameKana });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpInfo]);

  useEffect(() => {
    setIsPostalCodeSearchSkip(true);
    if (address && isEqual(address.status, COMMON_CONFIGS.API.STATUS_SUCCESS)) {
      const { prfNm, ctyOrCnt, lrgLtt, chr } = address.address;
      setRequest({
        ...request,
        paymentTargetCompanyAddress1: (prfNm + ctyOrCnt + lrgLtt + chr).trim(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  /**
   * 法人番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const corporateNumberOnChange = (inputValue: string) => {
    // 法人番号を編集した場合は事業者名、事業者名カナを空にする
    setRequest({
      ...request,
      corporateNumber: inputValue,
      companyName: "",
      companyNameKana: "",
    });
    setErrMsg({ ...errMsg, corporateNumber: validCorporateNumber(inputValue) });
  };

  /**
   * 法人番号検索ボタンを押したときの処理.
   */
  const corpNoSearchOnClick = () => {
    if (hasCorpNo && isUndefined(errMsg.corporateNumber)) {
      setIsCorpNoSearchSkip(false);
    }
  };

  /**
   * 事業者名の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const companyNameOnChange = (inputValue: string) => {
    setRequest({ ...request, companyName: inputValue });
    setErrMsg({
      ...errMsg,
      companyName: validCompanyName(inputValue, request),
    });
  };

  /**
   * 事業者名カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const companyNameKanaOnChange = (inputValue: string) => {
    setRequest({ ...request, companyNameKana: inputValue });
    setErrMsg({
      ...errMsg,
      companyNameKana: validCompanyNameKana(inputValue, request),
    });
  };

  /**
   * 郵便番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const postalCodeOnChange = (inputValue: string) => {
    setRequest({
      ...request,
      postalCode: inputValue,
    });
    setErrMsg({ ...errMsg, postalCode: validPostalCode(inputValue, request) });
  };

  /**
   * 郵便番号検索ボタンを押したときの処理.
   */
  const postalCodeSearchOnClick = () => {
    if (!isNull(request.postalCode) && isUndefined(errMsg.postalCode)) {
      setIsPostalCodeSearchSkip(false);
    }
  };

  /**
   * 本社住所（その他）の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const paymentTargetCompanyAddress2OnChange = (inputValue: string) => {
    setRequest({ ...request, paymentTargetCompanyAddress2: inputValue });
    setErrMsg({
      ...errMsg,
      paymentTargetCompanyAddress2: validPaymentTargetCompanyAddress2(
        inputValue,
        request
      ),
    });
  };

  /**
   * 本社住所カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const paymentTargetCompanyAddress1Kana = (inputValue: string) => {
    setRequest({ ...request, paymentTargetCompanyAddress1Kana: inputValue });
    setErrMsg({
      ...errMsg,
      paymentTargetCompanyAddress1Kana: validPaymentTargetCompanyAddress1Kana(
        inputValue,
        request
      ),
    });
  };

  /**
   * 電話番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const phoneNumberOnChange = (inputValue: string) => {
    setRequest({ ...request, phoneNumber: inputValue });
    setErrMsg({
      ...errMsg,
      phoneNumber: validPhoneNumber(inputValue, request),
    });
  };

  return (
    <SpaceBetween direction={"vertical"} size={"l"}>
      <FormField
        label={<Box variant={"strong"}>{LABELS.CORPORATE_NUMBER}</Box>}
        constraintText={CONSTRAINT_TEXT.CORPORATE_NUMBER}
        errorText={errMsg.corporateNumber}
      >
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <Input
            value={request.corporateNumber}
            onChange={({ detail: { value } }) => corporateNumberOnChange(value)}
          />

          <Button
            formAction={"none"}
            variant={"normal"}
            onClick={corpNoSearchOnClick}
          >
            {COMMON_LABELS.BTNS.SEARCH_CORP_NUM}
          </Button>
        </SpaceBetween>
      </FormField>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.COMPANY_NAME}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.COMPANY_NAME}
          errorText={errMsg.companyName}
        >
          <Input
            value={request.companyName}
            disabled={hasCorpNo}
            onChange={({ detail: { value } }) => companyNameOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.COMPANY_NAME_KANA}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.COMPANY_NAME_KANA}
          errorText={errMsg.companyNameKana}
        >
          <Input
            value={request.companyNameKana}
            disabled={hasCorpNo}
            onChange={({ detail: { value } }) => companyNameKanaOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
      <FormField
        label={
          <SpaceBetween direction={"horizontal"} size={"xs"}>
            <Box variant={"strong"}>{LABELS.POSTAL_CODE}</Box>
            {isRequired && (
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            )}
          </SpaceBetween>
        }
        constraintText={CONSTRAINT_TEXT.POSTAL_CODE}
        errorText={errMsg.postalCode}
      >
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <Input
            value={request.postalCode}
            onChange={({ detail: { value } }) => postalCodeOnChange(value)}
          />
          <Button
            formAction={"none"}
            variant={"normal"}
            onClick={postalCodeSearchOnClick}
          >
            {COMMON_LABELS.BTNS.SEARCH_POSTAL_CODE}
          </Button>
        </SpaceBetween>
      </FormField>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg.paymentTargetCompanyAddress1}
        >
          <Input value={request.paymentTargetCompanyAddress1} disabled={true} />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS_OTHERS}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.ADDRESS_OTHERS}
          errorText={errMsg.paymentTargetCompanyAddress2}
        >
          <Input
            value={request.paymentTargetCompanyAddress2}
            onChange={({ detail: { value } }) =>
              paymentTargetCompanyAddress2OnChange(value)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS_KANA}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.ADDRESS_KANA}
          errorText={errMsg.paymentTargetCompanyAddress1Kana}
        >
          <Input
            value={request.paymentTargetCompanyAddress1Kana}
            onChange={({ detail: { value } }) =>
              paymentTargetCompanyAddress1Kana(value)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.PHONE_NUMBER}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.PHONE_NUMBER}
          errorText={errMsg.phoneNumber}
        >
          <Input
            value={request.phoneNumber}
            onChange={({ detail: { value } }) => phoneNumberOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
    </SpaceBetween>
  );
};
